<script lang="ts" setup>
import {computed, type Component} from "vue";
import {ChevronDown, CircleMinus, CirclePlus, Gift, ReceiptText, RotateCw, ShoppingCart} from "@lucide/vue";
import {LoyaltyCardMembershipTransaction} from "@/loyalty-card/interfaces/hds/LoyaltyCard";
import {Button} from "@/core/components/ui/button";
import {Skeleton} from "@/core/components/ui/skeleton";

interface Emit {
    typeSelected: [type: string | null];
    loadMore: [];
    retry: [];
}

const emit = defineEmits<Emit>();

const props = defineProps<{
    transactions: LoyaltyCardMembershipTransaction[];
    selectedType: string | null;
    /** The first page is loading. */
    loading?: boolean;
    /** A next page is loading. */
    loadingMore?: boolean;
    failed?: boolean;
    hasMore?: boolean;
    /** Rendered inside a container that already frames and titles it (the history dialog). */
    embedded?: boolean;
}>();

const types = [
    {
        name: 'Tous',
        value: null
    },

    {
        name: 'Achats',
        value: 'purchase'
    },

    {
        name: 'Récompenses',
        value: 'reward'
    },

    {
        name: 'Crédits',
        value: 'credit'
    }
];

type TransactionKind = 'purchase' | 'reward' | 'credit' | 'adjustment';

const KINDS: Record<TransactionKind, { icon: Component; tile: string }> = {
    purchase: {icon: ShoppingCart, tile: 'bg-lightPurple-lightest text-primary'},
    reward: {icon: Gift, tile: 'bg-secondary-lightest text-secondary-darkest'},
    credit: {icon: CirclePlus, tile: 'bg-orange-light text-amber-800'},
    adjustment: {icon: CircleMinus, tile: 'bg-pink-light text-red-light'}
};

/**
 * A balance adjustment is told by its type; an HDS serving no type has none.
 * Otherwise only the objective entries (a purchase and the points it earns)
 * carry the reward type, and only rewards debit the card.
 */
const kindOf = (transaction: LoyaltyCardMembershipTransaction): TransactionKind => {
    if (transaction.type === 'loyaltyCardMembershipAdjustment') {
        return 'adjustment';
    }

    if (transaction.reward_type) {
        return 'purchase';
    }

    return transaction.sign === '-' ? 'reward' : 'credit';
};

const items = computed(() => props.transactions.map((transaction) => {
    // A points card sale that earned no point shows how far its objective got.
    const objectiveProgress = transaction.formatted.objective_progress;

    return {
        transaction,
        kind: KINDS[kindOf(transaction)],
        isNeutral: transaction.sign === '-' || !!objectiveProgress,
        signedValue: objectiveProgress
            ? '0 point'
            : `${transaction.sign === '-' ? '−' : '+'}${transaction.value}`,
        balanceLabel: objectiveProgress
            ? `${objectiveProgress} vers le prochain point`
            : `solde ${transaction.formatted.balance}`,
        detail: [
            transaction.description,
            transaction.product
                ? `${transaction.product.name}${transaction.product.code ? ` (${transaction.product.code})` : ''}`
                : null
        ].filter(Boolean).join(' · ')
    };
}));
</script>

<template>
  <div :class="{'bg-white overflow-hidden shadow rounded-lg mt-5': !embedded}">
    <div v-if="!embedded" class="bg-white px-4 py-5 border-b border-gray-200 sm:px-6">
      <h3 class="text-lg leading-6 font-medium text-gray-900">
        Historique
      </h3>
    </div>

    <div class="flex flex-col gap-3" :class="{'px-4 py-5 sm:p-6': !embedded}">
      <div role="group" aria-label="Type de mouvement" class="flex flex-wrap gap-2">
        <button
          v-for="type in types"
          :key="type.name"
          type="button"
          :aria-pressed="type.value === selectedType"
          class="px-3 py-1.5 rounded-full text-sm font-semibold border border-solid transition-colors"
          :class="type.value === selectedType
            ? 'border-primary bg-primary-lightest text-primary'
            : 'border-slate-200 bg-white text-slate-500 hover:border-gray'"
          @click="emit('typeSelected', type.value)"
        >
          {{ type.name }}
        </button>
      </div>

      <div v-if="loading" class="flex flex-col" aria-busy="true">
        <div v-for="index in 4" :key="index" class="flex gap-3 py-3 border-b border-gray-light last:border-b-0">
          <Skeleton class="size-8 shrink-0 rounded-lg" />
          <div class="flex-1 flex flex-col gap-2">
            <div class="flex items-center gap-2">
              <Skeleton class="h-3.5 w-32" />
              <span class="flex-1" />
              <Skeleton class="h-4 w-8" />
              <Skeleton class="h-4 w-16" />
            </div>
            <Skeleton class="h-3 w-3/5" />
          </div>
        </div>
      </div>

      <div v-else-if="failed && !transactions.length" class="flex flex-col items-center gap-3 py-10 text-center">
        <p class="text-base font-semibold text-gray-dark">
          L'historique n'a pas pu être chargé
        </p>
        <p class="text-sm text-slate-500">
          Vérifiez votre connexion et réessayez.
        </p>
        <Button variant="outline" size="sm" class="border-solid" @click="emit('retry')">
          <RotateCw />
          Réessayer
        </Button>
      </div>

      <div v-else-if="!transactions.length" class="flex flex-col items-center gap-3 py-10 text-center">
        <span class="size-14 rounded-2xl bg-lightPurple-lightest text-primary flex items-center justify-center">
          <ReceiptText class="size-6" />
        </span>
        <p class="text-base font-semibold text-gray-dark">
          {{ selectedType ? 'Aucun mouvement de ce type sur cette carte' : "Aucun mouvement sur cette carte pour l'instant" }}
        </p>
        <p v-if="!selectedType" class="max-w-sm text-sm text-slate-500">
          Les achats, crédits et récompenses apparaîtront ici dès la première vente enregistrée sur la carte.
        </p>
      </div>

      <template v-else>
        <ul>
          <li
            v-for="item in items"
            :key="item.transaction.id"
            class="flex gap-3 py-3 border-b border-gray-light last:border-b-0"
          >
            <span class="size-8 shrink-0 rounded-lg flex items-center justify-center" :class="item.kind.tile">
              <component :is="item.kind.icon" class="size-4" />
            </span>

            <div class="flex-1 min-w-0 flex flex-col gap-1.5">
              <div class="flex items-center gap-2">
                <span class="text-base font-semibold text-gray-dark">{{ item.transaction.title }}</span>
                <span class="text-sm text-slate-500">le {{ item.transaction.formatted.created_at }}</span>
                <span class="flex-1" />
                <span
                  class="px-2.5 py-0.5 rounded-md text-xs font-semibold tabular-nums"
                  :class="item.isNeutral ? 'bg-neutral text-gray-dark' : 'bg-[#d6f8ea] text-emerald-700'"
                >
                  {{ item.signedValue }}
                </span>
                <span
                  class="px-2.5 py-0.5 rounded-md border border-slate-200 text-xs font-semibold text-gray-dark whitespace-nowrap"
                >
                  {{ item.balanceLabel }}
                </span>
              </div>

              <ul v-if="item.transaction.purchases?.length" class="flex flex-col gap-1">
                <li
                  v-for="purchase in item.transaction.purchases"
                  :key="purchase.id"
                  class="flex items-center gap-2 text-sm"
                >
                  <span class="min-w-0 truncate font-medium text-gray-dark">
                    {{ purchase.quantity }} × {{ purchase.product?.name ?? "Produit inconnu" }}
                  </span>
                  <span
                    v-if="purchase.animation"
                    class="shrink-0 px-2.5 py-0.5 rounded-md bg-lightPurple-lightest text-primary text-xs font-semibold"
                  >
                    ×{{ parseFloat(String(purchase.animation.coefficient)) }} {{ purchase.animation.name }}
                  </span>
                  <span class="flex-1" />
                  <span class="shrink-0 text-slate-500 tabular-nums">{{ purchase.product?.code }}</span>
                  <span class="shrink-0 w-20 text-right font-semibold text-gray-dark tabular-nums">
                    {{ purchase.formatted.price }}
                  </span>
                </li>
              </ul>
              <p v-else-if="item.detail" class="text-sm text-slate-500">
                {{ item.detail }}
              </p>
            </div>
          </li>
        </ul>

        <div v-if="hasMore" class="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            class="text-primary hover:text-primary"
            :disabled="loadingMore"
            @click="emit('loadMore')"
          >
            {{ loadingMore ? 'Chargement…' : 'Afficher plus' }}
            <ChevronDown />
          </Button>
        </div>
      </template>
    </div>
  </div>
</template>
